import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';
import { productsApi } from '../lib/api';
import { trackAddToSelection, trackProductClosed } from '../lib/analytics';
import { Sheet, SheetContent } from './ui/sheet';
import { useTabletMode } from '../context/TabletModeContext';
import { X, Leaf, Wheat, MapPin, Award, Sparkles, Plus, Check, ChevronLeft, ChevronRight, Star, AlertCircle } from 'lucide-react';
import { AllergenSection } from './AllergenIcons';

// Placeholder images
const productPlaceholders = [
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80',
  'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=600&q=80',
  'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?w=600&q=80',
];

const addButtonTexts = {
  es: { add: 'Añadir', added: '¡Añadido!' },
  en: { add: 'Add', added: 'Added!' },
  cat: { add: 'Afegir', added: 'Afegit!' },
  fr: { add: 'Ajouter', added: 'Ajouté !' }
};

const breadSelectorTexts = {
  es: 'Escoge tu pan',
  en: 'Choose your bread',
  cat: 'Escull el teu pa',
  fr: 'Choisis ton pain'
};

const wineTypeTexts = {
  es: 'Selecciona el tipo de vino',
  en: 'Select the wine type',
  cat: 'Selecciona el tipus de vi',
  fr: 'Sélectionnez le type de vin'
};

const extrasTexts = {
  es: 'Opciones adicionales',
  en: 'Additional options',
  cat: 'Opcions addicionals',
  fr: 'Options supplémentaires'
};

const recommendTexts = {
  es: 'recomendamos',
  en: 'we recommend',
  cat: 'recomanem',
  fr: 'nous recommandons'
};

const emotionalLabels = {
  popular: {
    es: 'El más pedido hoy',
    en: 'Most ordered today',
    cat: 'El més demanat avui',
    fr: 'Le plus commandé aujourd\'hui'
  },
  recommended: {
    es: 'Recomendado por la casa',
    en: 'House recommendation',
    cat: 'Recomanat per la casa',
    fr: 'Recommandé par la maison'
  },
  chef: {
    es: 'Selección del chef',
    en: 'Chef\'s selection',
    cat: 'Selecció del xef',
    fr: 'Sélection du chef'
  }
};

const swipeHintTexts = {
  es: 'Desliza para ver más',
  en: 'Swipe to see more',
  cat: 'Llisca per veure\'n més',
  fr: 'Glissez pour voir plus'
};

const SWIPE_HINT_KEY = 'pont10_swipe_hint_shown';

export const ProductPopup = ({ product, onClose, onNavigate, hasPrev, hasNext, totalProducts = 0, currentIndex = 0, onNavigateToShop, onProductAdded }) => {
  const { language, localize, t } = useLanguage();
  const { addItem, lastAdded } = useSelection();
  const { isTablet } = useTabletMode();
  // recommendations state removed — loaded but never rendered, its async
  // setState was triggering an extra re-render mid-Sheet-animation on iPad.
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [justAdded, setJustAdded] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  
  // Touch handling refs
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndX = useRef(0);
  const isSwiping = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (product?.id) {
      setSelectedOptions([]);
      setJustAdded(false);
      setValidationError(null);
      
      // Check if we should show swipe hint
      const hintShown = localStorage.getItem(SWIPE_HINT_KEY);
      if (!hintShown && (hasPrev || hasNext)) {
        setShowSwipeHint(true);
        // Auto-hide after 3 seconds
        const timer = setTimeout(() => {
          setShowSwipeHint(false);
          localStorage.setItem(SWIPE_HINT_KEY, 'true');
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [product?.id]);

  useEffect(() => {
    if (lastAdded === product?.id) {
      setJustAdded(true);
      const timer = setTimeout(() => setJustAdded(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [lastAdded, product?.id]);

  // Touch handlers for swipe
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = false;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartX.current) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = Math.abs(currentX - touchStartX.current);
    const deltaY = Math.abs(currentY - touchStartY.current);
    
    // Only consider it a swipe if horizontal movement is greater than vertical
    if (deltaX > deltaY && deltaX > 10) {
      isSwiping.current = true;
      // Prevent vertical scroll when swiping horizontally
      if (deltaX > 30) {
        e.preventDefault();
      }
    }
    
    touchEndX.current = currentX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping.current) {
      touchStartX.current = 0;
      touchEndX.current = 0;
      return;
    }

    const deltaX = touchEndX.current - touchStartX.current;
    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > minSwipeDistance && !isAnimating) {
      if (deltaX > 0 && hasPrev) {
        // Swipe right -> previous product
        navigateWithAnimation('prev');
      } else if (deltaX < 0 && hasNext) {
        // Swipe left -> next product
        navigateWithAnimation('next');
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
    isSwiping.current = false;
    
    // Hide hint after first swipe
    if (showSwipeHint) {
      setShowSwipeHint(false);
      localStorage.setItem(SWIPE_HINT_KEY, 'true');
    }
  }, [hasPrev, hasNext, isAnimating, showSwipeHint]);

  const navigateWithAnimation = useCallback((direction) => {
    if (isAnimating) return;
    
    setSlideDirection(direction);
    setIsAnimating(true);
    
    // Trigger navigation after short delay for exit animation
    setTimeout(() => {
      onNavigate(direction);
      // Reset animation state after enter animation
      setTimeout(() => {
        setSlideDirection(null);
        setIsAnimating(false);
      }, 300);
    }, 150);
  }, [isAnimating, onNavigate]);

  const getProductImage = (prod, index = 0) => {
    // First check image_url (new field for generated images)
    if (prod?.image_url && prod.image_url.startsWith('http')) return prod.image_url;
    // Then check images array
    if (prod?.images?.[0]) return prod.images[0];
    return productPlaceholders[index % productPlaceholders.length];
  };

  const handleAddToSelection = () => {
    if (!product) return;
    
    // Check if product requires option selection in each group (Degustación para 2)
    if (product.requires_selection) {
      const options = product.options || [];
      const groups = {};
      options.forEach(opt => {
        if (opt.group) {
          if (!groups[opt.group]) groups[opt.group] = [];
          groups[opt.group].push(opt);
        }
      });
      
      const groupKeys = Object.keys(groups);
      const missingGroups = groupKeys.filter(group => 
        !selectedOptions.some(opt => opt.group === group)
      );
      
      if (missingGroups.length > 0) {
        setValidationError(product.restriction_message?.[language] || product.restriction_message?.es || 
          (language === 'es' ? 'Debes seleccionar una opción en cada grupo.' 
          : language === 'en' ? 'You must select an option in each group.'
          : language === 'cat' ? 'Has de seleccionar una opció a cada grup.'
          : 'Vous devez sélectionner une option dans chaque groupe.'));
        return;
      }
    }
    
    // Check if bread selection is required
    if (product.requires_bread_selection) {
      const hasBreadSelected = selectedOptions.some(opt => opt.group === 'tipo_pan');
      if (!hasBreadSelected) {
        setValidationError(language === 'es' ? 'Debes elegir un tipo de pan.' 
          : language === 'en' ? 'You must choose a bread type.'
          : language === 'cat' ? 'Has de triar un tipus de pa.'
          : 'Vous devez choisir un type de pain.');
        return;
      }
    }
    
    // Check if wine type selection is required
    if (product.requires_option_selection) {
      const hasTypeSelected = selectedOptions.some(opt => opt.group === 'tipo_vino');
      if (!hasTypeSelected) {
        setValidationError(language === 'es' ? 'Debes seleccionar el tipo de vino.'
          : language === 'en' ? 'You must select the wine type.'
          : language === 'cat' ? 'Has de seleccionar el tipus de vi.'
          : 'Vous devez sélectionner le type de vin.');
        return;
      }
    }
    
    setValidationError(null);
    trackAddToSelection(product, 1, selectedOptions);
    addItem(product, 1, selectedOptions);
    setJustAdded(true);
    
    // Notify parent about product added with a small delay for visual feedback
    setTimeout(() => {
      onClose();
      if (onProductAdded) {
        onProductAdded({...product, selectedOptions});
      }
    }, 400);
  };

  const handleClose = () => {
    if (product && popupOpenTime.current) {
      const timeSpent = Date.now() - popupOpenTime.current;
      trackProductClosed(product.id, timeSpent);
    }
    onClose();
  };

  // Track time spent on popup
  const popupOpenTime = useRef(null);
  useEffect(() => {
    if (product) {
      popupOpenTime.current = Date.now();
    }
  }, [product?.id]);

  const toggleOption = (option) => {
    setSelectedOptions(prev => {
      const optionName = localize(option.name);
      const exists = prev.find(o => localize(o.name) === optionName);
      
      // If option has a group, it's a single-select (radio) behavior
      if (option.group) {
        if (exists) {
          // Deselect - remove it
          return prev.filter(o => localize(o.name) !== optionName);
        }
        // Select - remove any other option from same group and add this one
        return [
          ...prev.filter(o => o.group !== option.group),
          option
        ];
      }
      
      // No group - multi-select (checkbox) behavior
      if (exists) {
        return prev.filter(o => localize(o.name) !== optionName);
      }
      return [...prev, option];
    });
  };

  const isOptionSelected = (option) => {
    return selectedOptions.some(o => localize(o.name) === localize(option.name));
  };

  // Group options by their group property
  const getGroupedOptions = (options) => {
    const grouped = {};
    const ungrouped = [];
    
    options.forEach(opt => {
      if (opt.group) {
        if (!grouped[opt.group]) {
          grouped[opt.group] = [];
        }
        grouped[opt.group].push(opt);
      } else {
        ungrouped.push(opt);
      }
    });
    
    return { grouped, ungrouped };
  };

  // Get display name for group — with recommendation hint
  const getGroupTitle = (group, groupOptions) => {
    const titles = {
      ensalada: { es: 'Elige tu ensalada', en: 'Choose your salad', cat: 'Tria la teva amanida', fr: 'Choisissez votre salade' },
      principal: { es: 'Elige tu plato principal', en: 'Choose your main dish', cat: 'Tria el teu plat principal', fr: 'Choisissez votre plat principal' },
      postre: { es: 'Elige tu postre', en: 'Choose your dessert', cat: 'Tria les teves postres', fr: 'Choisissez votre dessert' },
      tipo_pan: { es: 'Escoge tu pan', en: 'Choose your bread', cat: 'Escull el teu pa', fr: 'Choisis ton pain' },
      tipo_vino: { es: 'Selecciona el tipo de vino', en: 'Select the wine type', cat: 'Selecciona el tipus de vi', fr: 'Sélectionnez le type de vin' },
      tipo_bebida: { es: 'Elige tu bebida', en: 'Choose your drink', cat: 'Tria la teva beguda', fr: 'Choisissez votre boisson' },
      tipo_leche: { es: 'Tipo de leche', en: 'Milk type', cat: 'Tipus de llet', fr: 'Type de lait' },
      tipo_bikini: { es: 'Elige tu bikini', en: 'Choose your bikini', cat: 'Tria el teu bikini', fr: 'Choisissez votre bikini' },
    };
    const baseTitle = titles[group]?.[language] || titles[group]?.es || group;
    
    // Add recommendation hint with first option name
    if (groupOptions && groupOptions.length > 0) {
      const recommendedName = localize(groupOptions[0].name).replace(/^(Ensalada|Principal|Postre|Salad|Main|Dessert|Amanida|Postres|Pan|Bread|Pa|Vin|Wine|Vi):\s*/i, '');
      const recText = recommendTexts[language] || recommendTexts.es;
      return `${baseTitle} (${recText} ${recommendedName})`;
    }
    return baseTitle;
  };

  const calculateTotal = () => {
    if (!product?.price) return 0;
    const optionsExtra = selectedOptions.reduce(
      (acc, opt) => acc + (opt.price_modifier || 0), 0
    );
    return product.price + optionsExtra;
  };

  const buttonText = addButtonTexts[language] || addButtonTexts.es;

  if (!product) return null;

  // Animation classes
  const getContentAnimationClass = () => {
    if (!slideDirection) return 'animate-fade-in';
    if (slideDirection === 'next') return 'slide-out-left';
    return 'slide-out-right';
  };

  return (
    <Sheet open={!!product} onOpenChange={handleClose}>
      <SheetContent 
        side="bottom" 
        className="rounded-t-3xl p-0 border-0 overflow-hidden [&>button]:hidden"
        style={{
          backgroundColor: 'var(--sand-50)',
          maxWidth: '100vw',
          // On tablet leave a generous gap at the top so the Sheet never
          // sits behind the global TopCartBar (which is ~80-110px tall
          // including safe-area on iPad). On mobile a taller Sheet is fine
          // because TopCartBar is shorter.
          height: isTablet
            ? `calc(100dvh - env(safe-area-inset-top, 0px) - 140px)`
            : '90dvh',
          maxHeight: isTablet
            ? `calc(100dvh - env(safe-area-inset-top, 0px) - 140px)`
            : '90dvh',
        }}
        data-testid="product-popup"
      >
        {/* Handle bar */}
        <div className="sheet-handle" />

        {/* Close button - top center, always accessible */}
        <div className="absolute left-1/2 -translate-x-1/2 z-20" style={{ top: '14px' }}>
          <button
            onClick={handleClose}
            className="rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105"
            style={{ 
              width: '52px', height: '52px',
              backgroundColor: 'rgba(251, 245, 232, 0.98)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
              border: '1px solid rgba(45,42,38,0.12)',
            }}
            data-testid="close-popup-btn"
            aria-label="Cerrar"
          >
            <X size={26} style={{ color: 'var(--stone-800)' }} />
          </button>
        </div>

        {/* Main content — replaces Radix ScrollArea with a simple overflow div
            to avoid layout recalculations that caused the iPad open-flicker. */}
        <div
          className="h-full overflow-y-auto"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
            <div className="w-full max-w-full overflow-x-hidden">
              {/* Product Image */}
              <div
                className="relative w-full aspect-[16/10]"
                style={{ background: 'var(--sand-50)' }}
              >
                <img
                  src={getProductImage(product, product?.index || 0)}
                  alt={localize(product?.name) || ''}
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                  loading="eager"
                  decoding="async"
                />
                <div 
                  className="absolute bottom-0 left-0 right-0 h-24"
                  style={{ background: 'linear-gradient(to top, var(--sand-50), transparent)' }}
                />
              </div>

              {/* Content */}
              <div className="px-5 pb-8 -mt-8 relative z-10">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    {/* Premium Badge - Prominent display for authentic products */}
                    {product?.is_premium && (
                      <div className="mb-3">
                        <span 
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                          style={{ 
                            background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #B8860B 100%)',
                            color: '#FFF',
                            boxShadow: '0 2px 8px rgba(184, 134, 11, 0.3)'
                          }}
                        >
                          <Star size={14} fill="currentColor" />
                          {t('premium')}
                        </span>
                      </div>
                    )}
                    
                    {/* Dietary Badges */}
                    {(product?.is_vegan || product?.is_gluten_free || product?.is_local || product?.is_do_emporda) && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {product?.is_vegan && (
                          <span className="dietary-tag" style={{ backgroundColor: 'var(--gold)' }}>
                            <Leaf size={12} />
                            {t('vegan')}
                          </span>
                        )}
                        {product?.is_gluten_free && (
                          <span className="dietary-tag" style={{ backgroundColor: 'var(--kraft)' }}>
                            <Wheat size={12} />
                            {t('glutenFree')}
                          </span>
                        )}
                        {product?.is_local && (
                          <span className="dietary-tag" style={{ backgroundColor: 'var(--wine-500)' }}>
                            <MapPin size={12} />
                            {t('local')}
                          </span>
                        )}
                        {product?.is_do_emporda && (
                          <span className="dietary-tag" style={{ backgroundColor: 'var(--stone-800)' }}>
                            <Award size={12} />
                            {t('doEmporda')}
                          </span>
                        )}
                      </div>
                    )}

                    <h2 className="font-serif text-2xl font-medium text-stone-800 leading-tight">
                      {localize(product?.name) || ''}
                    </h2>
                    {/* Emotional label — from product badge_type (admin configured) */}
                    {product?.badge_type && product.badge_type !== 'none' && emotionalLabels[product.badge_type] && (
                      <span
                        data-testid="emotional-label"
                        style={{
                          fontSize: '12px',
                          background: 'rgba(200,155,60,0.15)',
                          border: '1px solid rgba(200,155,60,0.4)',
                          padding: '4px 8px',
                          borderRadius: '8px',
                          display: 'inline-block',
                          marginTop: '6px',
                          color: 'var(--stone-600)',
                          fontWeight: 500,
                        }}
                      >
                        {emotionalLabels[product.badge_type][language] || emotionalLabels[product.badge_type].es}
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                {product?.description && localize(product.description) && (
                  <p className="text-base leading-relaxed mb-4" style={{ color: 'var(--stone-600)' }}>
                    {localize(product.description)}
                  </p>
                )}

                {/* Allergens Section */}
                {product?.allergens && (
                  <div className="mb-6 border-t border-b" style={{ borderColor: 'rgba(183, 140, 60, 0.12)' }}>
                    <AllergenSection allergens={product.allergens} language={language} />
                  </div>
                )}

                {/* Shop availability message */}
                {product?.available_in_shop && (
                  <div 
                    className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl"
                    style={{ backgroundColor: 'rgba(107, 122, 82, 0.08)', border: '1px solid rgba(107, 122, 82, 0.15)' }}
                    data-testid="shop-availability-badge"
                  >
                    <span className="text-lg">&#127982;</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: '#5C5C59' }}>
                        {product.shop_message?.[language] || product.shop_message?.es || 
                          (language === 'es' ? 'Disponible en tienda — llévatelo a casa' :
                           language === 'en' ? 'Available in shop — take it home' :
                           language === 'cat' ? 'Disponible a botiga — emporta-te\'l a casa' :
                           'Disponible en boutique — emportez-le')
                        }
                      </p>
                    </div>
                  </div>
                )}

                {/* Options */}
                {product.options?.length > 0 && (() => {
                  const { grouped, ungrouped } = getGroupedOptions(product.options);
                  const groupKeys = Object.keys(grouped);
                  
                  return (
                    <div className="mb-6">
                      {/* Grouped options (single select per group) */}
                      {groupKeys.length > 0 && (
                        <div className="space-y-5">
                          <h4 className="font-serif text-lg font-medium" style={{ color: 'var(--stone-800)' }}>
                            {language === 'es' ? 'Personaliza tu elección' : 
                             language === 'en' ? 'Customize your choice' : 
                             'Personalitza la teva elecció'}
                          </h4>
                          {groupKeys.map(group => (
                            <div key={group} className="space-y-2">
                              <p className="text-sm font-medium" style={{ color: 'var(--stone-600)' }}>
                                {getGroupTitle(group, grouped[group])} <span className="text-xs opacity-60">(1)</span>
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {grouped[group].map((option, idx) => {
                                  const isRecommended = idx === 0;
                                  const isSelected = isOptionSelected(option);
                                  // Extract clean name (remove prefix like "Ensalada: ")
                                  const displayName = localize(option.name).replace(/^(Ensalada|Principal|Postre|Salad|Main|Dessert|Amanida|Postres):\s*/i, '');
                                  return (
                                    <button
                                      key={`${group}-${idx}`}
                                      onClick={() => toggleOption(option)}
                                      className={`option-btn ${isSelected ? 'selected' : ''}`}
                                      style={isRecommended && !isSelected ? {
                                        border: '2px solid #C89B3C',
                                        background: 'rgba(200,155,60,0.1)',
                                      } : undefined}
                                      data-testid={`option-${group}-${idx}`}
                                    >
                                      {displayName}
                                      {option.price_modifier !== 0 && (
                                        <span className="ml-2 opacity-80">
                                          {option.price_modifier > 0 ? '+' : ''}{option.price_modifier.toFixed(2)} €
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Ungrouped options (multi select) */}
                      {ungrouped.length > 0 && (
                        <div className={groupKeys.length > 0 ? 'mt-5' : ''}>
                          <h4 className="font-serif text-lg font-medium mb-3" style={{ color: 'var(--stone-800)' }}>
                            {t('options')}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {ungrouped.map((option, idx) => (
                              <button
                                key={idx}
                                onClick={() => toggleOption(option)}
                                className={`option-btn ${isOptionSelected(option) ? 'selected' : ''}`}
                                data-testid={`option-${idx}`}
                              >
                                {localize(option.name)}
                                {option.price_modifier !== 0 && (
                                  <span className="ml-2 opacity-80">
                                    {option.price_modifier > 0 ? '+' : ''}{option.price_modifier.toFixed(2)} €
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Supplements */}
                {product.supplements?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-serif text-lg font-medium mb-3" style={{ color: 'var(--stone-800)' }}>
                      {t('supplements')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {product.supplements.map((supp, idx) => (
                        <button
                          key={idx}
                          onClick={() => toggleOption(supp)}
                          className={`option-btn ${isOptionSelected(supp) ? 'selected' : ''}`}
                          data-testid={`supplement-${idx}`}
                        >
                          {localize(supp.name)}
                          {supp.price_modifier > 0 && (
                            <span className="ml-2 opacity-80">
                              +{supp.price_modifier.toFixed(2)} €
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bread note if applicable */}
                {product?.bread_note && (
                  <p className="text-sm italic mb-4" style={{ color: 'var(--stone-500)' }}>
                    {localize(product.bread_note)}
                  </p>
                )}

                {/* Validation Error */}
                {validationError && (
                  <div 
                    className="mb-4 p-4 rounded-xl flex items-start gap-3"
                    style={{ backgroundColor: 'rgba(139, 61, 63, 0.1)', border: '1px solid rgba(139, 61, 63, 0.2)' }}
                    data-testid="validation-error"
                  >
                    <AlertCircle size={20} className="shrink-0 mt-0.5" style={{ color: '#8B3D3F' }} />
                    <p className="text-sm font-medium" style={{ color: '#8B3D3F' }}>
                      {validationError}
                    </p>
                  </div>
                )}

                {/* ADD TO SELECTION BUTTON — irresistible CTA */}
                <button
                  onClick={handleAddToSelection}
                  className={`w-full rounded-full font-medium flex items-center justify-center gap-2 transition-all duration-500 mb-6 ${
                    justAdded ? 'scale-95' : 'scale-100 hover:scale-[1.02] active:scale-[0.98]'
                  }`}
                  style={{
                    background: justAdded 
                      ? 'var(--gold)' 
                      : 'linear-gradient(90deg, #8B4513, #C89B3C)',
                    color: '#FBF5E8',
                    borderRadius: '30px',
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: 500,
                    boxShadow: justAdded 
                      ? '0 5px 15px rgba(183, 140, 60, 0.3)' 
                      : '0 10px 30px rgba(0,0,0,0.2)',
                    border: 'none',
                    cursor: 'pointer',
                    letterSpacing: '0.02em',
                  }}
                  data-testid="add-to-selection-btn"
                >
                  {justAdded ? (
                    <>
                      <Check size={20} className="animate-scale-in" />
                      {buttonText.added}
                    </>
                  ) : (
                    <>
                      {buttonText.add} · {calculateTotal().toFixed(2)}€
                    </>
                  )}
                </button>

              </div>
            </div>
        </div>

      </SheetContent>
    </Sheet>
  );
};

// Recommendation card
const RecommendationCard = ({ product, index }) => {
  const { localize } = useLanguage();
  const { addItem, lastAdded } = useSelection();
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (lastAdded === product?.id) {
      setAdded(true);
      const timer = setTimeout(() => setAdded(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [lastAdded, product?.id]);

  // Guard against undefined product
  if (!product) return null;

  const handleAdd = (e) => {
    e.stopPropagation();
    addItem(product);
  };

  const getImage = () => {
    // First check image_url (new field for generated images)
    if (product?.image_url && product.image_url.startsWith('http')) return product.image_url;
    // Then check images array
    if (product?.images?.[0]) return product.images[0];
    return productPlaceholders[index % productPlaceholders.length];
  };

  return (
    <div 
      className="suggestion-card"
      data-testid={`cross-sell-${product.id}`}
    >
      <div 
        className="suggestion-card-img"
        style={{
          width: '100%',
          height: '90px',
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: 'var(--sand-200)',
          borderRadius: '0.5rem 0.5rem 0 0'
        }}
      >
        <img 
          src={getImage()}
          alt={localize(product?.name) || ''}
          loading="eager"
          decoding="async"
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            display: 'block'
          }}
          onError={(e) => {
            e.target.src = productPlaceholders[index % productPlaceholders.length];
          }}
        />
      </div>
      <div className="suggestion-card-body">
        <p className="suggestion-card-name">
          {localize(product?.name) || ''}
        </p>
        <div className="flex items-center justify-between">
          <span className="suggestion-card-price">
            {(product?.price ?? 0).toFixed(2)} €
          </span>
          <button
            onClick={handleAdd}
            className={`suggestion-add-btn ${added ? 'added' : ''}`}
          >
            {added ? <Check size={12} /> : <Plus size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductPopup;
